import { useCallback, useState, useMemo } from 'react';
import type { ActionMeta, MultiValue, SingleValue } from 'react-select';
import Select from 'react-select';
import { useQuery } from '@tanstack/react-query';
import type { BrandInsightsInstanceContext, ProjectFilter, RecordFilter } from '@trinity/chat';
import { ChatContextType, resolveSubjectScopedKind } from '@trinity/chat';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { CollapsedValueContainer } from './CollapsedValueContainer';
import { getFilterLabelForContext, getFilterPlaceholderForContext } from './filterLabelMapping.js';
import * as Presentation from './Presentation';

/**
 * Contexts with no project scope of their own repurpose this field into a record search
 * instead (Documents/Folders/Brands/Transcripts), stored as typed records. 'project' is
 * the default/fallback mode.
 */
type Mode = 'project' | 'project-subject' | 'documents' | 'folders' | 'brands' | 'transcripts';

const RECORD_TYPE_BY_MODE: Record<Exclude<Mode, 'project' | 'project-subject'>, string> = {
  documents: 'file',
  folders: 'folder',
  brands: 'brand',
  transcripts: 'transcript',
};

// Agent+Project is subject-scoped like Document/Folder/Brand/Transcript, checked
// first so it takes precedence over the plain 'project' multi-select fallback.
function resolveMode(context: BrandInsightsInstanceContext.ItemWithMetadata): Mode {
  const kind = resolveSubjectScopedKind(context);
  switch (kind) {
    case 'project':
      return context.type === ChatContextType.Agent ? 'project-subject' : 'project';
    case 'document':
      return 'documents';
    case 'folder':
      return 'folders';
    case 'brand':
      return 'brands';
    case 'transcript':
      return 'transcripts';
    default:
      return 'project';
  }
}

export type Props = {
  context: BrandInsightsInstanceContext.ItemWithMetadata;
  value: ProjectFilter.Item[];
  onChange: (value: ProjectFilter.Item[], meta: ActionMeta<ProjectFilter.Item>) => unknown;
  /** Full `filters.records` array; this field only edits the slice matching its current mode's type. */
  records: RecordFilter.Item[];
  onRecordsChange: (records: RecordFilter.Item[]) => unknown;
  /**
   * Sets/clears the active Agent context's `subject`. Agent contexts only — the query
   * pipeline reads `context.data.subject`, never `filters.records`, to scope the chat.
   */
  onSubjectSelected: (subject: { id: string; name: string } | null) => unknown;
  disabled?: boolean;
};

export function Project({
  disabled,
  context,
  value,
  onChange,
  records,
  onRecordsChange,
  onSubjectSelected,
}: Props) {
  const theme = useTheme();

  const mode = useMemo(() => resolveMode(context), [context]);
  const isProjectSubjectMode = mode === 'project-subject';
  const isRecordMode = mode !== 'project' && !isProjectSubjectMode;
  const recordType = isRecordMode ? RECORD_TYPE_BY_MODE[mode] : null;
  // Agent contexts are single-subject (see onSubjectSelected); non-Agent record-mode
  // contexts keep the pre-existing multi-select records behavior below.
  const isAgentContext = context.type === ChatContextType.Agent;

  const selectStyles = useMemo(() => Presentation.buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => Presentation.buildSelectTheme(theme), [theme]);

  const selectedProjects = useMemo(() => value, [value]);
  const selectedRecords = useMemo(
    () => (recordType ? records.filter((record) => record.type === recordType) : []),
    [records, recordType]
  );
  // Source of truth for Agent-context record modes is the chat's active `subject`,
  // not `filters.records` (which the query pipeline never reads for these contexts).
  const selectedSubject = useMemo((): RecordFilter.Item | null => {
    if (
      !isAgentContext ||
      context.type !== ChatContextType.Agent ||
      !recordType ||
      // subject may be undefined in some staging data states
      !context.data.subject?.id ||
      !context.data.subject?.name
    ) {
      return null;
    }
    return {
      id: `${context.data.subject.id}`,
      name: context.data.subject.name,
      type: recordType,
    };
  }, [context, isAgentContext, recordType]);

  // Agent+Project counterpart of `selectedSubject` above: active `subject`, not `filters.projects`.
  const selectedProjectSubject = useMemo((): ProjectFilter.Item | null => {
    if (
      !isProjectSubjectMode ||
      context.type !== ChatContextType.Agent ||
      // subject may be undefined in some staging data states
      !context.data.subject?.id ||
      !context.data.subject?.name
    ) {
      return null;
    }
    return {
      id: Number(context.data.subject.id),
      name: context.data.subject.name,
    };
  }, [context, isProjectSubjectMode]);

  const handleProjectChange = useCallback(
    (value: MultiValue<ProjectFilter.Item>, meta: ActionMeta<ProjectFilter.Item>) => {
      onChange(Array.from(value), meta);
    },
    [onChange]
  );

  const handleRecordChange = useCallback(
    (value: MultiValue<RecordFilter.Item>) => {
      const otherTypeRecords = recordType ? records.filter((record) => record.type !== recordType) : records;
      onRecordsChange([...otherTypeRecords, ...Array.from(value)]);
    },
    [onRecordsChange, records, recordType]
  );

  const handleSubjectChange = useCallback(
    (newValue: SingleValue<RecordFilter.Item>) => {
      onSubjectSelected(newValue ? { id: newValue.id, name: newValue.name } : null);
    },
    [onSubjectSelected]
  );

  const handleProjectSubjectChange = useCallback(
    (newValue: SingleValue<ProjectFilter.Item>) => {
      onSubjectSelected(newValue ? { id: String(newValue.id), name: newValue.name } : null);
    },
    [onSubjectSelected]
  );

  const [inputValue, setInputValue] = useState('');
  const [emptyDisabled, setEmptyDisabled] = useState(false);

  const apiClient = useChatApiClient();
  const activeBrandIdentifier = useActiveBrandIdentifier();

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const projectsQuery = useQuery({
    queryKey: [`get:brand-insights/chat/filters/projects`, context, inputValue],
    queryFn: async () => {
      const result = await apiClient.chat.searchChatProjects({
        context,
        query: inputValue,
      });

      setEmptyDisabled(result.empty);

      return result.results;
    },
    placeholderData: [],
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 5000,
    enabled: !disabled && (mode === 'project' || isProjectSubjectMode),
  });

  const searchByMode = useCallback(
    async (searchMode: Exclude<Mode, 'project'>, query: string) => {
      // Reuses the header's own global, access-controlled context search.
      const recordTypeByMode: Record<Exclude<Mode, 'project' | 'project-subject'>, ChatContextType> = {
        documents: ChatContextType.Document,
        folders: ChatContextType.Folder,
        brands: ChatContextType.Brand,
        transcripts: ChatContextType.Transcript,
      };
      const contextType = recordTypeByMode[searchMode];
      if (!contextType) {
        return [];
      }

      const isAgent = context.type === ChatContextType.Agent;
      const res = await apiClient.chat.contextSearch({
        q: query,
        type: isAgent ? ChatContextType.Agent : contextType,
        contextIdentifier: isAgent ? context.data.identifier : '',
        brandIdentifier: activeBrandIdentifier ?? '',
      });
      return res.data.results.map((item) => ({
        id: `${item.entityId}`,
        name: item.name,
        type: RECORD_TYPE_BY_MODE[searchMode],
      }));
    },
    [apiClient, context, activeBrandIdentifier]
  );

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const recordsQuery = useQuery({
    queryKey: [`get:brand-insights/chat/filters/${mode}`, context, inputValue, activeBrandIdentifier],
    queryFn: () => searchByMode(mode as Exclude<Mode, 'project'>, inputValue),
    placeholderData: [],
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 5000,
    enabled: !disabled && isRecordMode,
  });

  const handleInputChange = useCallback((newValue: string) => setInputValue(newValue), []);

  const handleNoOptionsMessage = useCallback(
    (obj: { inputValue: string }) => {
      if (isRecordMode) {
        if (recordsQuery.isFetching) return `Loading...`;
        return obj.inputValue?.length ? `No ${mode} found` : null;
      }
      if (projectsQuery.isFetching) return `Loading...`;
      return obj.inputValue?.length ? `No projects found` : null;
    },
    [isRecordMode, mode, projectsQuery.isFetching, recordsQuery.isFetching]
  );

  // Derive label/placeholder dynamically from context type
  // Must be after all other hooks to avoid hook order violation
  const label = useMemo(() => getFilterLabelForContext(context), [context]);
  const placeholder = useMemo(() => getFilterPlaceholderForContext(context), [context]);

  // If filter not applicable for this context, don't render
  if (!label) return null;

  if (isProjectSubjectMode) {
    return (
      <Presentation.Row>
        <Presentation.Label>{label}</Presentation.Label>
        <Presentation.Value>
          <Select<ProjectFilter.Item, false>
            styles={selectStyles}
            components={{ DropdownIndicator: null }}
            theme={selectTheme}
            value={selectedProjectSubject}
            isMulti={false}
            isClearable
            options={projectsQuery.data}
            placeholder={placeholder}
            noOptionsMessage={handleNoOptionsMessage}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => `${o.id}`}
            onChange={handleProjectSubjectChange}
            onInputChange={handleInputChange}
            isDisabled={disabled}
            menuPlacement="auto"
          />
        </Presentation.Value>
      </Presentation.Row>
    );
  }

  if (isRecordMode) {
    const options = recordsQuery.data ?? [];

    if (isAgentContext) {
      return (
        <Presentation.Row>
          <Presentation.Label>{label}</Presentation.Label>
          <Presentation.Value>
            <Select<RecordFilter.Item, false>
              styles={selectStyles}
              components={{ DropdownIndicator: null }}
              theme={selectTheme}
              value={selectedSubject}
              isMulti={false}
              isClearable
              options={options}
              placeholder={placeholder}
              noOptionsMessage={handleNoOptionsMessage}
              getOptionLabel={(o) => o.name}
              getOptionValue={(o) => o.id}
              onChange={handleSubjectChange}
              onInputChange={handleInputChange}
              isDisabled={disabled}
              menuPlacement="auto"
            />
          </Presentation.Value>
        </Presentation.Row>
      );
    }

    return (
      <Presentation.Row>
        <Presentation.Label>{label}</Presentation.Label>
        <Presentation.Value>
          <Select<RecordFilter.Item, true>
            styles={selectStyles}
            components={{
              DropdownIndicator: null,
              ValueContainer: CollapsedValueContainer,
            }}
            theme={selectTheme}
            value={selectedRecords}
            isMulti
            options={options}
            placeholder={placeholder}
            noOptionsMessage={handleNoOptionsMessage}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.id}
            onChange={handleRecordChange}
            onInputChange={handleInputChange}
            isDisabled={disabled}
            menuPlacement="auto"
          />
        </Presentation.Value>
      </Presentation.Row>
    );
  }

  return (
    <Presentation.Row>
      <Presentation.Label>{label}</Presentation.Label>
      <Presentation.Value>
        <Select<ProjectFilter.Item, true>
          styles={selectStyles}
          components={{
            DropdownIndicator: null,
            ValueContainer: CollapsedValueContainer,
          }}
          theme={selectTheme}
          value={selectedProjects}
          isMulti
          options={projectsQuery.data}
          placeholder={placeholder}
          noOptionsMessage={handleNoOptionsMessage}
          getOptionLabel={(o) => o.name}
          getOptionValue={(o) => `${o.id}`}
          onChange={handleProjectChange}
          onInputChange={handleInputChange}
          isDisabled={disabled || emptyDisabled}
          menuPlacement="auto"
        />
      </Presentation.Value>
    </Presentation.Row>
  );
}
